// App
import { type ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';

// Actions
import AdActions from 'app/shared/flux/actions/AdActions';

// Hooks
import { usePrevious } from 'app/shared/modules/MobileSRP/ListingCards/List/hooks';
import { useAds } from './useAds';

// Helpers
import {
  getAdvertisementConfigurationsHelper,
  getAdvertisementSlotIdsHelper,
} from 'app/shared/modules/AdvertisementWrapper/helpers';

// Misc / styles
import { SkeletonCard as AdvertisementSkeleton } from 'app/shared/modules/listing-cards/ListingCardV3';
import { ListingAdContainer, ListingAdListItem } from './styles';

type AdConfig = {
  name: string;
  index: number;
  placement: string;
  format: string;
  sizes: unknown;
  type: string;
};

type PreviousProps = {
  gadsInit: boolean;
  shouldRefreshAdSlot1: boolean;
  shouldRefreshAdSlot2: boolean;
};

type AdvertisementWrapperProps = {
  handleHideAdElement: (slot: 'slot1' | 'slot2', hideNativeAd: boolean) => void;
  children: Array<ReactNode>;
  hideSlot1?: boolean;
  hideSlot2?: boolean;
};

const SLOT_1_INDEX = 2;
const SLOT_2_INDEX = 17;
const AdvertisementWrapper = ({ handleHideAdElement, children, hideSlot1, hideSlot2 }: AdvertisementWrapperProps) => {
  // Redux
  const dispatch = useDispatch();
  const gadsInit = useSelector((state: RootReduxState) => state.ads.gadsInit);
  const isClientLoaded = useSelector((state: RootReduxState) => state.app.clientLoaded);
  const filter = useSelector((state: RootReduxState) => state.filter);
  const shouldRefreshAdSlot1 = useSelector((state: RootReduxState) => state.ads?.srpAdSlot1?.shouldRefresh);
  const shouldRefreshAdSlot2 = useSelector((state: RootReduxState) => state.ads?.srpAdSlot2?.shouldRefresh);

  const adConfigs = getAdvertisementConfigurationsHelper(filter);
  const adConfig1 = adConfigs[0] as AdConfig;
  const adConfig2 = adConfigs[1] as AdConfig;
  const adSlotIds = getAdvertisementSlotIdsHelper([adConfig1, adConfig2]);
  const adSlot1Id = adSlotIds[0] as string;
  const adSlot2Id = adSlotIds[1] as string;

  // Hooks
  const prevProps = usePrevious({ gadsInit, shouldRefreshAdSlot1, shouldRefreshAdSlot2 }) as PreviousProps | undefined;
  const [defineAd, refreshAd, cleanUpAd] = useAds({
    handleHideAdElement,
    filter,
    registeredConfigurations: [adConfig1, adConfig2],
    registeredIds: [adSlot1Id, adSlot2Id],
  });

  // componentDidMount
  useEffect(() => {
    dispatch(AdActions.loadGoogleAdApi());

    // @ts-expect-error - useAds hook types not yet defined
    defineAd();

    return () => {
      // componentWillUnmount
      // @ts-expect-error - useAds hook types not yet defined
      cleanUpAd();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate
  useEffect(() => {
    if (prevProps?.gadsInit === false && gadsInit === true) {
      // @ts-expect-error - useAds hook types not yet defined
      defineAd();
    }
    if (
      (prevProps?.shouldRefreshAdSlot1 === false && shouldRefreshAdSlot1 === true) ||
      (prevProps?.shouldRefreshAdSlot2 === false && shouldRefreshAdSlot2 === true)
    ) {
      // @ts-expect-error - useAds hook types not yet defined
      refreshAd([adConfig1?.placement, adConfig2?.placement]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gadsInit, shouldRefreshAdSlot1, shouldRefreshAdSlot2]);

  const AdOne = (
    <ListingAdListItem
      key={isClientLoaded ? `list-item-container-for-${adSlot1Id}` : `list-item-ad-container-for-${adSlot1Id}`}
    >
      {isClientLoaded ? <ListingAdContainer id={adSlot1Id} /> : <AdvertisementSkeleton />}
    </ListingAdListItem>
  );

  const AdTwo = (
    <ListingAdListItem
      key={isClientLoaded ? `list-item-container-for-${adSlot2Id}` : `list-item-ad-container-for-${adSlot2Id}`}
    >
      {isClientLoaded ? <ListingAdContainer id={adSlot2Id} /> : <AdvertisementSkeleton />}
    </ListingAdListItem>
  );

  const augmentedChildren = [...children];

  if (!hideSlot1) {
    augmentedChildren.splice(SLOT_1_INDEX, 0, AdOne);
  }

  if (!hideSlot2) {
    augmentedChildren.splice(SLOT_2_INDEX, 0, AdTwo);
  }

  return augmentedChildren;
};

export default AdvertisementWrapper;
